import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@/hooks/use-theme";
import { AuthProvider } from "@/hooks/use-auth";
import { GeoOverrideProvider } from "@/hooks/useGeoOverride";
import { ViewAsProvider } from "@/hooks/useViewAs";
import { AdminToolbar } from "@/components/AdminToolbar";
import { GlobalPresence } from "@/components/GlobalPresence";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AskSuperAnalystFloating } from "@/components/AskSuperAnalystFloating";
import { Suspense } from "react";
import { lazy } from "@/lib/lazyWithReload";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { RouteProgressBar } from "@/components/RouteProgressBar";
import { RoutePrefetcher } from "@/components/RoutePrefetcher";
import { VisitCount } from "@/lib/visitCount";
import { ReferralCapture } from "@/components/ReferralCapture";

// Redirect old /research-in-action/* URLs to the new /ai-investing-club/* slug.
// Returns 200 on the SPA but issues a Navigate(replace) so back-button is clean,
// and bots hitting /research-in-action via the og-meta function get a true 301.
const RIASlugRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/ai-investing-club/${slug ?? ""}`} replace />;
};
const RIACommunityRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`/ai-investing-club/community${search}${hash}`} replace />;
};
const RIARootRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`/ai-investing-club${search}${hash}`} replace />;
};

// Hot routes: eagerly imported so navigation feels instant. These are the
// pages users hit constantly; lazy-loading them was causing the visible
// 2-4s "old page lingers" lag while the chunk downloaded.
import Services from "./pages/Services";
import ResearchInAction from "./pages/ResearchInAction";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";

// Lazy-loaded routes (code-split out of the main bundle to improve LCP on landing page).
// UX is unchanged — each route fetches its chunk on navigation, identical to a normal import.
const About = lazy(() => import("./pages/About"));
// The newsletter lives on Substack (his ruling, 27 Oct 2026); an in-app link to /newsletter goes there.
const ToSubstack = () => {
  window.location.replace("https://ai.shikshannivesh.com/");
  return null;
};
const Founder = lazy(() => import("./pages/Founder"));
const Admin = lazy(() => import("./pages/Admin"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PaymentVerify = lazy(() => import("./pages/PaymentVerify"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentFailed = lazy(() => import("./pages/PaymentFailed"));

const AiFinancialResearch = lazy(() => import("./pages/AiFinancialResearch"));
const VerifyBooking = lazy(() => import("./pages/VerifyBooking"));
const BookingSuccess = lazy(() => import("./pages/BookingSuccess"));
const QuickChat = lazy(() => import("./pages/QuickChat"));
const FreeDiscoveryCall = lazy(() => import("./pages/FreeDiscoveryCall"));
const BookingConfirmed = lazy(() => import("./pages/BookingConfirmed"));
const Legal = lazy(() => import("./pages/Legal"));
const AiResearchConsultation = lazy(() => import("./pages/AiResearchConsultation"));
const CommandCentreSuccess = lazy(() => import("./pages/CommandCentreSuccess"));
const VerifyCommandCentre = lazy(() => import("./pages/VerifyCommandCentre"));
const AgenticResearchSystems = lazy(() => import("./pages/AgenticResearchSystems"));
const ResearchContentDetail = lazy(() => import("./pages/ResearchContentDetail"));
const SubscriptionVerify = lazy(() => import("./pages/SubscriptionVerify"));
const Enterprise = lazy(() => import("./pages/Enterprise"));
const Community = lazy(() => import("./pages/Community"));
const ShareYourExperience = lazy(() => import("./pages/ShareYourExperience"));
const TestimonialsWall = lazy(() => import("./pages/TestimonialsWall"));
const AdminTestimonials = lazy(() => import("./pages/AdminTestimonials"));
const FoundingMembers = lazy(() => import("./pages/FoundingMembers"));
const FoundingVerify = lazy(() => import("./pages/FoundingVerify"));
const Contact = lazy(() => import("./pages/Contact"));
const Refer = lazy(() => import("./pages/Refer"));
const ResearchDesk = lazy(() => import("./pages/ResearchDesk"));
const DatasetPage = lazy(() => import("./pages/DatasetPage"));
// The data platform (24 Oct 2026): the US market page, the deep dataset pages, the companies.
const PlatformMarket = lazy(() => import("./pages/platform/Market"));
const PlatformDataset = lazy(() => import("./pages/platform/Dataset"));
const PlatformCompany = lazy(() => import("./pages/platform/Company"));
const PlatformFiling = lazy(() => import("./pages/platform/Filing"));
const PlatformCompanies = lazy(() => import("./pages/platform/Companies"));
const PlatformScreener = lazy(() => import("./pages/platform/Screener"));
const PlatformDatasets = lazy(() => import("./pages/platform/Datasets"));
const PlatformDevelopers = lazy(() => import("./pages/platform/Developers"));
const PlatformAi = lazy(() => import("./pages/platform/Ai"));
const PlatformFreeData = lazy(() => import("./pages/platform/FreeData"));
const PlatformExcel = lazy(() => import("./pages/platform/Excel"));
const PlatformConnect = lazy(() => import("./pages/platform/Connect"));
const PlatformIndexHub = lazy(() => import("./pages/platform/Hub").then((m) => ({ default: m.IndexHub })));
const PlatformSectorHub = lazy(() => import("./pages/platform/Hub").then((m) => ({ default: m.SectorHub })));
const PlatformExchangeHub = lazy(() => import("./pages/platform/Hub").then((m) => ({ default: m.ExchangeHub })));
// preview drafts, local dev server only: never in a production build
const PlatformCompare = import.meta.env.DEV ? lazy(() => import("./pages/platform/ComparePreview").then((m) => ({ default: m.ComparePreview }))) : null;
const PlatformProviders = import.meta.env.DEV ? lazy(() => import("./pages/platform/ComparePreview").then((m) => ({ default: m.ProvidersPreview }))) : null;
const PlatformFollow = lazy(() => import("./pages/platform/Follow"));
// the 13F funds (30 Oct 2026): every fund, and one fund's holdings and moves
const PlatformFunds = lazy(() => import("./pages/platform/Funds").then((m) => ({ default: m.FundList })));
const PlatformFund = lazy(() => import("./pages/platform/Funds").then((m) => ({ default: m.Fund })));
const PlatformCoverage = lazy(() => import("./pages/platform/Infra").then((m) => ({ default: m.Coverage })));
const PlatformChangelog = lazy(() => import("./pages/platform/Infra").then((m) => ({ default: m.Changelog })));
const PlatformStatus = lazy(() => import("./pages/platform/Infra").then((m) => ({ default: m.Status })));
const PLATFORM_SLUGS = ["events", "insider-trades", "planned-insider-sales", "large-holders", "congress-trades", "short-interest", "financial-statements", "fund-holdings-13f"];
const DataSlug = () => {
  const { slug = "" } = useParams();
  // keyed by the slug, so moving from one dataset to another starts the page afresh (a dataset's
  // rows never meet another dataset's columns)
  return PLATFORM_SLUGS.includes(slug) ? <PlatformDataset key={slug} /> : <DatasetPage key={slug} />;
};
/** A page that reads its own file starts afresh when the address names another company or list. */
const Keyed = ({ el: El, k }: { el: React.ComponentType; k: string }) => { const p = useParams(); return <El key={p[k] || ""} />; };
const DataLicence = lazy(() => import("./pages/DataLicence"));
const Filings = lazy(() => import("./pages/Filings"));
const FilingGuide = lazy(() => import("./pages/FilingGuide"));
const Congress = lazy(() => import("./pages/Congress"));
const CongressMember = lazy(() => import("./pages/CongressMember"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ViewAsProvider>
        <AuthProvider>
          <GlobalPresence />
          <GeoOverrideProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <div>
                <BrowserRouter>
                  <ScrollToTop />
                  <RouteProgressBar />
                  <RoutePrefetcher />
                  <VisitCount />
                  <Suspense
                    fallback={
                      <div
                        className="min-h-screen bg-background flex items-center justify-center"
                        role="status"
                        aria-label="Loading page"
                      >
                        <div className="h-8 w-8 rounded-full border border-[var(--sn-border-strong)] border-t-accent animate-spin" />
                      </div>
                    }
                  >
                    <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/products" element={<Services />} />
                    <Route path="/mentorship" element={<Navigate to="/services" replace />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/newsletter" element={<ToSubstack />} />
                    <Route path="/shubham-borkar" element={<Founder />} />
                    <Route path="/ai-investing-club" element={<ResearchInAction />} />
                    <Route path="/ai-investing-club/community" element={<Community />} />
                    <Route path="/ai-investing-club/refer" element={<Refer />} />
                    {/* Legacy slug → 301-style client redirects (og-meta serves 301 for bots) */}
                    <Route path="/research-in-action" element={<RIARootRedirect />} />
                    <Route path="/research-in-action/community" element={<RIACommunityRedirect />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/payment/verify" element={<PaymentVerify />} />
                    <Route path="/payment-success" element={<PaymentSuccess />} />
                    <Route path="/payment-failed" element={<PaymentFailed />} />
                    <Route path="/data" element={<PlatformMarket />} />
                    <Route path="/data/companies" element={<PlatformCompanies />} />
                    <Route path="/data/screener" element={<PlatformScreener />} />
                    <Route path="/data/datasets" element={<PlatformDatasets />} />
                    <Route path="/data/developers" element={<PlatformDevelopers />} />
                    <Route path="/data/ai" element={<PlatformAi />} />
                    <Route path="/data/free-stock-data" element={<PlatformFreeData />} />
                    <Route path="/data/excel" element={<PlatformExcel />} />
                    <Route path="/data/connect" element={<PlatformConnect />} />
                    <Route path="/data/coverage" element={<PlatformCoverage />} />
                    <Route path="/data/follow" element={<PlatformFollow />} />
                    <Route path="/data/funds" element={<PlatformFunds />} />
                    <Route path="/data/funds/:slug" element={<Keyed el={PlatformFund} k="slug" />} />
                    <Route path="/data/index/:slug" element={<Keyed el={PlatformIndexHub} k="slug" />} />
                    <Route path="/data/sector/:slug" element={<Keyed el={PlatformSectorHub} k="slug" />} />
                    <Route path="/data/exchange/:slug" element={<Keyed el={PlatformExchangeHub} k="slug" />} />
                    {PlatformCompare && <Route path="/data/compare/:vendor" element={<PlatformCompare />} />}
                    {PlatformProviders && <Route path="/data/providers" element={<PlatformProviders />} />}
                    <Route path="/data/changelog" element={<PlatformChangelog />} />
                    <Route path="/data/status" element={<PlatformStatus />} />
                    <Route path="/data/companies/:ticker" element={<Keyed el={PlatformCompany} k="ticker" />} />
                    <Route path="/data/companies/:ticker/filings/:accn" element={<PlatformFiling />} />
                    <Route path="/data/licence" element={<DataLicence />} />
                    <Route path="/data/:slug" element={<DataSlug />} />
                    <Route path="/filings" element={<Filings />} />
                    <Route path="/filings/:slug" element={<FilingGuide />} />
                    <Route path="/congress" element={<Congress />} />
                    <Route path="/congress/:slug" element={<CongressMember />} />
                    <Route path="/research-desk" element={<ResearchDesk />} />
                    <Route path="/product" element={<Navigate to="/research-desk" replace />} />
                    <Route path="/ai-financial-research" element={<AiFinancialResearch />} />
                    <Route path="/payment/verify-booking" element={<VerifyBooking />} />
                    <Route path="/booking-success" element={<BookingSuccess />} />
                    {/* Quick Chat stays reachable by its address but is listed nowhere (his ruling, 1 Nov 2026). */}
                    <Route path="/quick-chat" element={<QuickChat />} />
                    <Route path="/free-discovery-call" element={<FreeDiscoveryCall />} />
                    <Route path="/booking-confirmed" element={<BookingConfirmed />} />
                    <Route path="/legal" element={<Legal />} />
                    <Route path="/privacy-policy" element={<Legal />} />
                    <Route path="/terms" element={<Legal />} />
                    <Route path="/refund-policy" element={<Legal />} />
                    <Route path="/risk-disclosure" element={<Legal />} />
                    <Route path="/disclaimer" element={<Legal />} />
                    <Route path="/ai-research-consultation" element={<AiResearchConsultation />} />
                    <Route path="/command-centre" element={<Navigate to="/services" replace />} />
                    <Route path="/command-centre-pro" element={<AiResearchConsultation />} />
                    <Route path="/command-centre-success" element={<CommandCentreSuccess />} />
                    <Route path="/payment/verify-command-centre" element={<VerifyCommandCentre />} />
                    {/* Mentorship, Agentic AI Consultation and the Command Centre were retired on 1 Nov 2026 (his ruling); their
                        addresses forward to the catalogue. Command Centre payment returns stay for past buyers. */}
                    <Route path="/agentic-ai-consultation" element={<Navigate to="/services" replace />} />
                    <Route path="/vibe-engineering" element={<Navigate to="/services" replace />} />
                    <Route path="/agentic-research-systems" element={<AgenticResearchSystems />} />
                    <Route path="/ai-investing-club/:slug" element={<ResearchContentDetail />} />
                    <Route path="/research-in-action/:slug" element={<RIASlugRedirect />} />
                    {/* The masterclass pages were retired on 26 Oct 2026 (his ruling); their addresses forward to the catalogue. */}
                    <Route path="/masterclass" element={<Navigate to="/services" replace />} />
                    <Route path="/masterclass/*" element={<Navigate to="/services" replace />} />
                    <Route path="/subscription/verify" element={<SubscriptionVerify />} />
                    <Route path="/enterprise" element={<Enterprise />} />
                    <Route path="/enterprise/product" element={<Navigate to="/research-desk" replace />} />
                    <Route path="/share-your-experience" element={<ShareYourExperience />} />
                    <Route path="/testimonials" element={<TestimonialsWall />} />
                    <Route path="/admin/testimonials" element={<AdminTestimonials />} />
                    <Route path="/ai-investing-club/founding-members" element={<FoundingMembers />} />
                    <Route path="/founding/verify" element={<FoundingVerify />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/contact-us" element={<Contact />} />
                    <Route path="/support" element={<Contact />} />
                    <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                  <AdminToolbar />
                  <AskSuperAnalystFloating />
                  <ReferralCapture />
                </BrowserRouter>
              </div>
            </TooltipProvider>
          </GeoOverrideProvider>
        </AuthProvider>
        </ViewAsProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
